// 261005 E3-P4a — logic THUẦN của chặng ① sửa chương thô (không import antd để test được dưới jest). Quyết định dò/khoá/tác động nằm ở BE; đây chỉ là cách HIỆN.
import type { AssistantRequest } from './workbench/assistant/assistantIntent';
import type { RawChapter, RawChapterFinding, RawChapterImpact, RawChapterVersionMeta } from '../../types/adaptation';
import { friendlyApiError } from './workbench/jobErrorText';

export type FindingTone = 'ERROR' | 'WARN' | null;

const activeFindings = (c: Pick<RawChapter, 'findings'>): RawChapterFinding[] => (c.findings ?? []).filter((f) => !f.accepted);

/** Mức nặng nhất của chương (ERROR > WARN). */
export const chapterTone = (c: Pick<RawChapter, 'findings'>): FindingTone =>
  activeFindings(c).some((f) => f.severity === 'ERROR') ? 'ERROR' : activeFindings(c).length ? 'WARN' : null;

export const findingColor = (tone: FindingTone): string | undefined => (tone === 'ERROR' ? '#cf1322' : tone === 'WARN' ? '#d48806' : undefined);

export const filterProblemChapters = (chapters: RawChapter[], onlyProblems: boolean): RawChapter[] =>
  onlyProblems ? chapters.filter((c) => chapterTone(c) !== null) : chapters;

/** F2 — bộ lọc "Chỉ chương có vấn đề" chỉ có hiệu lực khi CÒN chương có vấn đề (hết lỗi sau Áp xoá ⇒ tự hiện đủ bảng, không trống). */
export const effectiveOnlyProblems = (chapters: RawChapter[], onlyProblems: boolean): boolean => onlyProblems && problemCount(chapters) > 0;
/** F2 — câu bảng rỗng: chưa có chương (nạp/thêm) khác với "không còn chương có vấn đề". */
/** F20 — mất kết nối / lỗi máy chủ (không phải 404 "chưa có"). Dùng để KHÔNG hiện "truyện trống" khi thật ra chưa tải được. */
export const isLoadFailure = (e: unknown): boolean => {
  const status = (e as { response?: { status?: number } } | null | undefined)?.response?.status;
  return typeof status !== 'number' || status >= 500;
};

export const emptyChaptersText = (total: number, onlyProblems: boolean, loadFailed = false): string =>
  total > 0 && onlyProblems ? 'Không còn chương có vấn đề' :
    total === 0 && loadFailed ? 'Không tải được danh sách chương — bấm "Thử lại".' : 'Chưa có chương nào — dán link ở trên hoặc bấm "+ Thêm chương".';

export const problemCount = (chapters: RawChapter[]): number => chapters.filter((c) => chapterTone(c) !== null).length;

/** F69 — tách/gộp tạo chương không còn URL gốc; khoá ngay tại dòng thay vì để người dùng bấm rồi im lặng. */
export const rawChapterReloadDisabledReason = (chapter: Pick<RawChapter, 'link'>): string | undefined =>
  chapter.link ? undefined : 'Chương đã mất link nguồn — không tải lại được';

// ─── F21/F25 — cột "Tình trạng": nhãn ngắn + chi tiết trong tooltip; rác lặp cả truyện gộp một dòng ─────────────────

const FINDING_SHORT: Record<string, string> = {
  ENDS_MID_SENTENCE: 'Cụt giữa câu', ENDS_ELLIPSIS: 'Kết bằng "…"', REPEATED_JUNK: 'Rác lặp', NEAR_DUPLICATE: 'Trùng một phần', PAYWALL_SUSPECT: 'Nghi bị khoá',
  EMPTY_CONTENT: 'Rỗng', DUPLICATE_CONTENT: 'Trùng nguyên văn', DUPLICATE_ORDER: 'Trùng thứ tự', ABNORMALLY_SHORT: 'Quá ngắn', KNOWN_JUNK: 'Dính rác site', JUNK_NAV: 'Rác điều hướng', HTML_RESIDUE: 'Sót thẻ HTML',
};

/** Nhãn NGẮN để đặt trong ô (không kèm đoạn trích — đoạn trích dài là thứ làm chữ đè lên cột nút). Mã lạ ⇒ rút gọn câu của BE. */
export const findingShortLabel = (f: { code: string; message: string; severity?: string }): string => {
  if (FINDING_SHORT[f.code]) return FINDING_SHORT[f.code];
  const m = (f.message ?? '').trim();
  return m.length > 24 ? `${m.slice(0, 23)}…` : m || f.code;
};

/** Dòng chữ đầy đủ cho tooltip: câu của BE · đoạn trích (nếu câu chưa chứa) · chương liên quan. */
export const findingDetailLines = (f: RawChapterFinding): string[] => {
  const out = [f.message];
  const ev = (f.evidence ?? '').trim();
  if (ev && !f.message.includes(ev)) out.push(`Đoạn: «${ev}»`);
  if (f.related?.length) out.push(`Cùng nội dung với chương: ${[...f.related].sort((a, b) => a - b).join(', ')}`);
  if (f.accepted) out.push(`Đã chấp nhận bởi ${f.accepted.acceptedBy} lúc ${f.accepted.acceptedAt}`);
  return out;
};

export interface RepeatedJunkSummary { count: number; total: number; text: string; detail: string }
/**
 * Rác lặp dính từ `minChapters` chương trở lên ⇒ MỘT dòng ở đầu bảng ("Rác lặp ở 20/20 chương: «…»") thay vì lặp ở mọi hàng. Ít hơn ⇒ null (hiện ở từng hàng).
 */
export const repeatedJunkSummary = (chapters: Array<Pick<RawChapter, 'findings'>>, minChapters = 3): RepeatedJunkSummary | null => {
  const hit = chapters.map((c) => c.findings?.find((f) => f.code === 'REPEATED_JUNK')).filter((f): f is RawChapterFinding => !!f);
  if (hit.length < minChapters) return null;
  const ev = (hit.find((f) => f.evidence?.trim())?.evidence ?? '').trim();
  const detail = hit[0].message.replace(/\s*[—-]\s*cũng có ở chương.*$/i, '').trim();
  return { count: hit.length, total: chapters.length, text: `Rác lặp ở ${hit.length}/${chapters.length} chương${ev ? `: «${ev}»` : ''}`, detail };
};

/** Finding hiện ở hàng: nếu rác lặp đã gộp lên đầu bảng thì bỏ khỏi hàng. */
export const rowFindings = (c: Pick<RawChapter, 'findings'>, junkAggregated: boolean): RawChapterFinding[] =>
  (c.findings ?? []).filter((f) => !(junkAggregated && f.code === 'REPEATED_JUNK'));

const NOTE_OP: Record<string, string> = { RELOAD: 'tải lại', SPLIT: 'tách', MERGE: 'gộp', DELETE: 'xoá', EDIT: 'sửa', RESTORE: 'khôi phục', TRIM: 'cắt rác' };
/** F28 — khoá nạp lại danh sách phiên bản chương: gộp thao tác tại chỗ (`versionsKey`), mọi lần ghi toàn màn (`dataVersion`, kể cả áp/khôi phục từ khung Trợ lý) và số chương. */
export const versionsRefreshKey = (versionsKey: number, dataVersion: number, chapterCount: number): string => `${versionsKey}:${dataVersion}:${chapterCount}`;

/** F25 — ghi chú phiên bản do BE ghi ("Khôi phục phiên bản 1 (RELOAD)") ⇒ chữ người đọc ("… (tải lại)"). */
export const versionNoteText = (note: string | null | undefined): string =>
  String(note ?? '').replace(/\((RELOAD|SPLIT|MERGE|DELETE|EDIT|RESTORE|TRIM)\)/g, (_m, op: string) => `(${NOTE_OP[op]})`);

export const findingsText = (fs: RawChapterFinding[] | undefined): string => (fs ?? []).map((f) => f.message).join(' · ');

export const LOCKED_TOOLTIP = 'Thuộc tập đã viết';

/** "Gộp với chương sau" cần có chương sau (theo thứ tự đọc `orderIndex`). */
export const hasNextChapter = (chapters: RawChapter[], id: string): boolean => {
  const o = [...chapters].sort((a, b) => a.orderIndex - b.orderIndex || (a.id < b.id ? -1 : 1));
  const i = o.findIndex((c) => c.id === id);
  return i >= 0 && i + 1 < o.length;
};

/** Câu cảnh báo trước khi áp, từ `impact` của `dryRun`. Rỗng ⇒ không có hệ quả hạ nguồn. */
export const impactLines = (i: RawChapterImpact | null | undefined): string[] => {
  if (!i) return [];
  const out: string[] = [];
  if (i.volumesStale.length) out.push(`Quyển ${i.volumesStale.join(', ')} cần quét lại`);
  if (i.episodesTouched.length) {
    const from = Math.min(...i.episodesTouched);
    out.push(`Danh sách tập cần chia lại từ tập ${from} (tập bị đụng: ${i.episodesTouched.join(', ')}) — khung xương giữ trạng thái duyệt`);
  }
  return out;
};

const OP_LABEL: Record<string, string> = { RELOAD: 'Tải lại', SPLIT: 'Tách', MERGE: 'Gộp', DELETE: 'Xoá', EDIT: 'Sửa', RESTORE: 'Khôi phục', TRIM: 'Cắt rác' };
export const versionOpLabel = (op: string): string => OP_LABEL[op] ?? op;

/** Chỉ bản mới nhất (versionNo lớn nhất) khôi phục được — BE trả 409 `VERSION_NOT_LATEST` cho bản cũ. */
export const latestVersionId = (versions: RawChapterVersionMeta[]): string | null =>
  versions.length ? versions.reduce((a, b) => (b.versionNo > a.versionNo ? b : a)).id : null;

/** Cửa sổ ứng viên quanh giữa chương: tối đa `max` ứng viên gần giữa nhất, theo thứ tự offset. */
export const candidatesNearMiddle = <T extends { offset: number }>(cands: T[], length: number, max = 12): T[] =>
  [...cands].sort((a, b) => Math.abs(a.offset - length / 2) - Math.abs(b.offset - length / 2)).slice(0, max).sort((a, b) => a.offset - b.offset);

/** Có lỗi BE nào có `code` này không (axios). */
export const rawErrorCode = (e: any): string | undefined => e?.response?.data?.code;
export const rawErrorText = (e: any, fallback = 'Không chạy được'): string => friendlyApiError(e, fallback);

/** Câu cảnh báo ở xem trước tải lại (rỗng ⇒ không có). */
export const reloadWarningText = (p: { warning?: string; removedParagraphs?: number } | null | undefined): string => {
  if (!p) return '';
  if (p.warning === 'BOILERPLATE_NOT_CHECKED') return 'Chưa lọc được rác của site — xem kỹ đầu / cuối trước khi lưu';
  return p.removedParagraphs ? `Đã bỏ ${p.removedParagraphs} đoạn rác lặp của site (học từ chương lân cận)` : '';
};

/** Id chip BE của finding (khớp `sourceChipId` của `utils/sourceAssistant.ts`): `raw:<chapterId>:<code>`. */
export const sourceChipIdOf = (chapterId: string, code: string): string => `raw:${chapterId}:${code}`;

export interface OptionsAction { intent: AssistantRequest; disabledReason?: string }

/**
 * 261005 E3-P4b1 — nút "Đưa ra phương án (tốn AI)" ở dòng chương có finding: mở khung Trợ lý ①, tick đúng lỗi, TỰ GỬI "3 phương án".
 * Chương không có finding ⇒ null (không nút). Chương khoá ⇒ có nút nhưng tắt kèm lý do.
 */
export const optionsActionFor = (c: Pick<RawChapter, 'id' | 'orderIndex' | 'findings' | 'lockedByScript'>): OptionsAction | null => {
  const fs = activeFindings(c);
  if (!fs.length) return null;
  const intent: AssistantRequest = {
    stageKey: 'source', issueIds: fs.map((f) => sourceChipIdOf(c.id, f.code)), autoSend: 'options',
    suggestedText: `Chương ${c.orderIndex} có vấn đề: ${findingsText(fs)}. Đưa ra các phương án khác cách (tải lại từ link / đổi cấu trúc / sửa tay) cho chương ${c.orderIndex}.`,
  };
  return { intent, ...(c.lockedByScript ? { disabledReason: `${LOCKED_TOOLTIP} — không đổi được chương này` } : {}) };
};
